// filepath: apps/web/src/features/admin/audit/AuditFilterBar.tsx
//
// FR-UI-ADM-14 — Filter controls for the Definition Audit Log Viewer.
//
// FR-UI-CORE-9: native <label htmlFor> + <select> / <input> semantics.
// AL-2..7: definition_type select, author text, after/before date inputs.
// AL-15: "Clear filters" resets all filter state.

import { useTranslation } from "@/i18n/useTranslation";
import {
  DEFINITION_TYPES,
  emptyFilter,
  type AuditFilterState,
  type DefinitionTypeFilter,
} from "./types";

export interface AuditFilterBarProps {
  filter: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
}

export function AuditFilterBar({ filter, onChange }: AuditFilterBarProps) {
  const { t } = useTranslation();
  return (
    <form
      className="audit-filter-bar"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className="audit-filter-bar__field">
        <label htmlFor="audit-filter-definition-type">
          {t("audit.filter.definition_type")}
        </label>
        <select
          id="audit-filter-definition-type"
          value={filter.definition_type}
          onChange={(e) =>
            onChange({
              ...filter,
              definition_type: e.target.value as DefinitionTypeFilter,
              cursor: null,
            })
          }
        >
          <option value="all">{t("audit.filter.all_types")}</option>
          {DEFINITION_TYPES.map((dt) => (
            <option key={dt} value={dt}>
              {t(`audit.column.definition_type.${dt}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="audit-filter-bar__field">
        <label htmlFor="audit-filter-author">{t("audit.filter.author")}</label>
        <input
          id="audit-filter-author"
          type="text"
          value={filter.author}
          onChange={(e) =>
            onChange({ ...filter, author: e.target.value, cursor: null })
          }
        />
      </div>

      <div className="audit-filter-bar__field">
        <label htmlFor="audit-filter-after">{t("audit.filter.after")}</label>
        <input
          id="audit-filter-after"
          type="datetime-local"
          value={filter.after ? filter.after.slice(0, 16) : ""}
          onChange={(e) =>
            onChange({
              ...filter,
              after: e.target.value
                ? new Date(e.target.value).toISOString()
                : null,
              cursor: null,
            })
          }
        />
      </div>

      <div className="audit-filter-bar__field">
        <label htmlFor="audit-filter-before">{t("audit.filter.before")}</label>
        <input
          id="audit-filter-before"
          type="datetime-local"
          value={filter.before ? filter.before.slice(0, 16) : ""}
          onChange={(e) =>
            onChange({
              ...filter,
              before: e.target.value
                ? new Date(e.target.value).toISOString()
                : null,
              cursor: null,
            })
          }
        />
      </div>

      <button
        type="button"
        onClick={() => onChange(emptyFilter())}
        aria-label={t("audit.filter.clear")}
      >
        {t("audit.filter.clear")}
      </button>
    </form>
  );
}
